import { Menu } from "@design-system/Menu/Menu.lib";
import type { MenuForcedState } from "@design-system/Menu/Menu.lib";
import { EditIcon, FolderIcon, TrashIcon } from "@icons";
import { OpenMenuCell } from "./MenuPlaygroundCell";
import {
    StatefulRadioGroup,
    StatefulToggleItem,
} from "./MenuPlaygroundStateful";
import { VariantGrid } from "./playgroundControls";

interface StateRow {
    label: string;
    forcedState?: MenuForcedState;
    isDisabled?: boolean;
    isChecked?: boolean;
}

const stateRows: readonly StateRow[] = [
    { label: "Resting" },
    { label: "Highlighted", forcedState: "hover" },
    { label: "Pressed", forcedState: "pressed" },
    { label: "Disabled", isDisabled: true },
];

const submenuStateRows: readonly StateRow[] = [
    ...stateRows.slice(0, 3),
    { label: "Submenu open", forcedState: "open" },
    ...stateRows.slice(3),
];

const toggleStateRows: readonly StateRow[] = [
    { label: "On", isChecked: true },
    { label: "Off" },
    { label: "Highlighted", forcedState: "hover", isChecked: true },
    { label: "Pressed", forcedState: "pressed" },
    { label: "Disabled on", isDisabled: true, isChecked: true },
    { label: "Disabled off", isDisabled: true },
];

const radioStateRows: readonly StateRow[] = [
    { label: "Selected" },
    { label: "Unselected" },
    ...stateRows.slice(1),
];

/**
 * Every row state: plain and destructive items, with and without a leading
 * icon, then submenu triggers, toggle rows and radio rows.
 */
export const MenuPlaygroundStates = () => (
    <VariantGrid title="States" columns="three">
        {[false, true].map((isDestructive) => (
            <OpenMenuCell
                key={String(isDestructive)}
                label={isDestructive ? "destructive" : "default"}
            >
                {stateRows.map(({ label, forcedState, isDisabled }) => (
                    <Menu.Item
                        key={label}
                        leading={isDestructive ? <TrashIcon /> : <EditIcon />}
                        destructive={isDestructive}
                        disabled={isDisabled}
                        data-forced-state={forcedState}
                    >
                        {label}
                    </Menu.Item>
                ))}
            </OpenMenuCell>
        ))}
        <OpenMenuCell label="with description">
            {stateRows.map(({ label, forcedState, isDisabled }) => (
                <Menu.Item
                    key={label}
                    description="Secondary line"
                    disabled={isDisabled}
                    data-forced-state={forcedState}
                >
                    {label}
                </Menu.Item>
            ))}
        </OpenMenuCell>
        <OpenMenuCell label="submenu trigger">
            {submenuStateRows.map(({ label, forcedState, isDisabled }) => (
                <Menu.Submenu
                    key={label}
                    leading={<FolderIcon />}
                    label={label}
                    disabled={isDisabled}
                    data-forced-state={forcedState}
                >
                    <Menu.Item>Nested item</Menu.Item>
                </Menu.Submenu>
            ))}
        </OpenMenuCell>
        <OpenMenuCell label="toggle item">
            {toggleStateRows.map(
                ({ label, forcedState, isDisabled, isChecked }) => (
                    <StatefulToggleItem
                        key={label}
                        initial={isChecked}
                        disabled={isDisabled}
                        data-forced-state={forcedState}
                    >
                        {label}
                    </StatefulToggleItem>
                ),
            )}
        </OpenMenuCell>
        <OpenMenuCell label="radio item (check reserves its slot)">
            <StatefulRadioGroup initial="Selected">
                {radioStateRows.map(({ label, forcedState, isDisabled }) => (
                    <Menu.RadioItem
                        key={label}
                        value={label}
                        disabled={isDisabled}
                        data-forced-state={forcedState}
                    >
                        {label}
                    </Menu.RadioItem>
                ))}
            </StatefulRadioGroup>
        </OpenMenuCell>
    </VariantGrid>
);
